<template>
  <div data-testid="materiality-table">
    
    <!-- Loading skeleton: shown during any loading state (initial load, filter, pagination) -->
    <div v-show="loading">
      <lb-datatablenew
        ref="skeletonTable"
        data-testid="materiality-table__loading-skeleton"
        :headers="headers"
        :items="shimmerItems"
        :loading="false"
        :disableFilters="true"
        :bypassFiltering="true"
        :enableslot="shimmerSlots"
        :hidePagination="true"
        multiselectkey="_id"
        :disableCheckbox="true"
        class="rounded-lg"
      >
        <template v-for="slot in shimmerSlots" v-slot:[slot]>
          <v-skeleton-loader :key="slot" class="my-1" type="text" />
        </template>
      </lb-datatablenew>
    </div>

    <!-- Data table: always mounted so internal filter state persists.
         Uses v-show to hide during loading and when no data exists.
         Stays visible when filters are active (even with 0 results) so users can clear them. -->
    <div v-show="!loading && (hasData || hasActiveFilters)">
      <lb-datatablenew
        ref="dataTable"
        data-testid="materiality-table__datatable"
        :headers="headers"
        :items="items"
        :enableslot="['action', 'additionalinfo', 'code']"
        :loading="loading"
        :disableFilters="loading || filtersDisabled"
        multiselectkey="_id"
        :disableCheckbox="loading"
        :selecteditems="[]"
        @loadNextPage="$emit('loadNextPage', $event)"
        @update:filters="handleFilterUpdate"
        @filter-menu-open="$emit('filter-menu-open', $event)"
        :currentPage="page"
        :serverSidePagination="true"
        :serverTotalCount="meta.totalCount || 0"
        :perpage="perpage"
        :hidePagination="false"
        :paginationOverAll="true"
        :paginationOverAllinline="false"
        :bypassFiltering="true"
      >
    
      <!-- Bulk action slot for multiselect -->
      <template v-slot:additionalinfo="{ value, selected, selectall }">
        <v-tooltip 
          bottom 
          content-class="tooltip-bottom" 
          v-if="selectall || (selected && selected.length > 0)"
        >
          <template v-slot:activator="{ on, attrs }">
            <v-btn 
              icon 
              v-bind="attrs" 
              v-on="on"
              @click="handleBulkEdit(value, 'Actual', selected)"
              data-testid="materiality-table__bulk-edit-actual-btn"
            >
              <img 
                  src="/img/icons/EditIcon.svg" 
                  class="icon-size-16" 
                  alt="Edit-Actual" 
                />
            </v-btn>
          </template>
          <span>Bulk update for {{ actualLabel }} Cycle</span>
        </v-tooltip>
        <v-tooltip 
          bottom 
          content-class="tooltip-bottom" 
          v-if="selectall || (selected && selected.length > 0)"
        >
          <template v-slot:activator="{ on, attrs }">
            <v-btn 
              icon 
              v-bind="attrs" 
              v-on="on"
              @click="handleBulkEdit(value, 'Forecast', selected)"
              data-testid="materiality-table__bulk-edit-forecast-btn"
            >
             <img 
                  src="/img/icons/EditIcon.svg" 
                  class="icon-size-16" 
                  alt="Edit-Forecast" 
                />
            </v-btn>
          </template>
          <span>Bulk update for {{ forecastLabel }} Cycle</span>
        </v-tooltip>
      </template>

      <!-- Action column slot -->
       <template v-slot:code="{ item }">
        {{ item.code }} - {{ item.name }}
       </template>
      <template v-slot:action="{ item }">
        <div 
          class="d-flex gap-4" 
          data-testid="materiality-table__action-cell"
        >
          <!-- Edit Actual -->
          <v-tooltip 
            bottom 
            content-class="tooltip-bottom" 
            v-if="hasEditRight"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-btn 
                icon 
                v-bind="attrs" 
                v-on="on"
                @click="handleSingleEdit(item, 'Actual')"
                data-testid="materiality-table__edit-actual-btn"
              >
                <img 
                  src="/img/icons/EditIcon.svg" 
                  class="icon-size-16" 
                  alt="Edit-Actual" 
                />
              </v-btn>
            </template>
            <span>{{ actualLabel }} Cycle</span>
          </v-tooltip>

          <!-- Edit Forecast -->
          <v-tooltip 
            bottom 
            content-class="tooltip-bottom" 
            v-if="hasEditRight"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-btn 
                icon 
                v-bind="attrs" 
                v-on="on"
                @click="handleSingleEdit(item, 'Forecast')"
                data-testid="materiality-table__edit-forecast-btn"
              >
                <img 
                  src="/img/icons/EditIcon.svg" 
                  class="icon-size-16" 
                  alt="Edit-Forecast" 
                />
              </v-btn>
            </template>
            <span>{{ forecastLabel }} Cycle</span>
          </v-tooltip>

          <!-- View Details -->
          <v-tooltip bottom content-class="tooltip-bottom">
            <template v-slot:activator="{ on, attrs }">
              <v-btn 
                icon 
                v-bind="attrs" 
                v-on="on"
                @click="handleViewDetails(item)"
                data-testid="materiality-table__view-details-btn"
              >
                <v-icon>mdi-eye</v-icon>
              </v-btn>
            </template>
            <span>View materiality</span>
          </v-tooltip>
        </div>
      </template>
    </lb-datatablenew>
    </div>

    <!-- Empty State: shown only when not loading, no data, and no active column filters.
         When filters return 0 results the table stays visible so users can modify/clear filters. -->
    <NoDataState
      v-if="!loading && !hasData && !hasActiveFilters"
      data-testid="materiality-table__empty-state"
      :title="UI_CONSTANTS.NO_DATA_TITLE"
      :description="UI_CONSTANTS.NO_DATA_DESCRIPTION"
      :icon="UI_CONSTANTS.NO_DATA_ICON"
      :iconSize="UI_CONSTANTS.NO_DATA_ICON_SIZE"
      iconColor="grey"
    />

    <!-- Pagination Controls -->
    <!-- <Pagination
      v-if="totalPages > 1"
      :value="page + 1"
      :lengthOverride="totalPages"
      :itemsperpage="perpage"
      @input="handlePageChange"
      data-testid="materiality-table__pagination"
    /> -->
  </div>
</template>

<script>
import NoDataState from '../NoDataState.vue';
// import Pagination from '../../../../views/common/Pagination.vue';
import { VARIANCE_ANALYSIS_UI } from '../../utils/constants';

export default {
  name: 'MaterialityTable',
  components: {
    NoDataState,
    // Pagination,
  },
  props: {
    /**
     * Table headers configuration
     */
    headers: {
      type: Array,
      required: true,
    },
    /**
     * Table data items
     */
    items: {
      type: Array,
      default: () => [],
    },
    /**
     * Loading state
     */
    loading: {
      type: Boolean,
      default: false,
    },
    /**
     * Whether raw data exists (used to show table even when formatted items might be empty)
     */
    hasData: {
      type: Boolean,
      default: false,
    },
    /**
     * Whether column filters are currently active.
     * When true and hasData is false, the table stays visible so users
     * can see and clear their filter selections instead of seeing the empty state.
     */
    hasActiveFilters: {
      type: Boolean,
      default: false,
    },
    /**
     * Whether user has edit permission
     */
    hasEditRight: {
      type: Boolean,
      default: false,
    },
    /**
     * Label for Actual cycle (from nova labels)
     */
    actualLabel: {
      type: String,
      default: 'Actual',
    },
    /**
     * Label for Forecast cycle (from nova labels)
     */
    forecastLabel: {
      type: String,
      default: 'Forecast',
    },
    page: {
      type: Number,
      default: 0,
    },
    meta: {
      type: Object,
      default: () => ({}),
    },
    perpage: {
      type: Number,
      default: 50,
    },
    /**
     * When true, column filter buttons show a loading spinner (e.g. while hierarchy data is loading)
     */
    filtersDisabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      UI_CONSTANTS: VARIANCE_ANALYSIS_UI,
      shimmerSlots: ['indexno', 'code', 'network', 'region', 'country', 'action'],
    };
  },
  computed: {
    /**
     * Calculate total number of pages based on meta totalCount and perpage
     */
    totalPages() {
      if (!this.meta?.totalCount) return 1;
      return Math.ceil(this.meta.totalCount / this.perpage);
    },
    /**
     * Generates shimmer/skeleton placeholder items for loading state
     * @returns {Array} Array of placeholder items for skeleton loader display
     */
    shimmerItems() {
      return Array.from({ length: 20 }, (_, index) => ({
        _id: `shimmer-${index}`,
        indexno: '',
        code: '',
        name: '',
        network: '',
        region: '',
        country: '',
        action: '',
      }));
    },
  },
  methods: {
    /**
     * Handle page change from pagination or go-to input
     * @param {number} newPage - The page number to navigate to
     */
    handlePageChange(newPage) {
      // Input newPage is 1-based from Pagination component
      let pageOneBased = parseInt(newPage, 10);

      // Validate bounds (1 to totalPages)
      if (pageOneBased > this.totalPages) {
        pageOneBased = this.totalPages;
      } else if (pageOneBased < 1) {
        pageOneBased = 1;
      }

      // Convert to 0-based index for API
      // If user clicks page 2, we want index 1
      const pageIndex = pageOneBased - 1;
      
      console.log('Requested page change:', newPage, 'Converted to index:', pageIndex);

      if (pageIndex !== this.page) {
        this.$emit('update:page', pageIndex);
        this.$emit('loadNextPage', { page: pageIndex });
      }
    },
    /**
     * Handle single item edit action
     * @param {Object} item - The item to edit
     * @param {string} cycleType - 'Actual' or 'Forecast'
     */
    handleSingleEdit(item, cycleType) {
      this.$emit('edit-data', {
        item,
        cycleType,
        editType: 'single',
        ruId: item._id,
        isViewMode: false,
      });
    },
    /**
     * Handle bulk edit action for selected items
     * @param {Object} item - Current item context
     * @param {string} cycleType - 'Actual' or 'Forecast'
     * @param {Array} selected - Array of selected item IDs
     */
    handleBulkEdit(item, cycleType, selected) {
      this.$emit('edit-data', {
        item,
        cycleType,
        editType: 'bulk',
        selected,
        isViewMode: false,
      });
    },
    /**
     * Handle view details action (view-only mode)
     * @param {Object} item - The item to view
     */
    handleViewDetails(item) {
      this.$emit('edit-data', {
        item,
        cycleType: 'Actual',
        editType: 'single',
        ruId: item._id,
        isViewMode: true,
      });
    },
    /**
     * Handle filter update from datatable and forward to parent
     * @param {Object} filters - Filter object containing column filter selections
     */
    handleFilterUpdate(filters) {
      this.$emit('update:filters', filters);
    },
    setExternalFilterItems(key, items) {
      this.$refs.dataTable?.setExternalFilterItems(key, items);
    },
    syncFilterStateToSkeleton() {
      const dataTable = this.$refs.dataTable;
      const skeletonTable = this.$refs.skeletonTable;
      if (dataTable && skeletonTable) {
        skeletonTable.selectedFilterItems = { ...dataTable.selectedFilterItems };
        skeletonTable.showFilter = [...dataTable.showFilter];
      }
    },
  },
  watch: {
    loading(newVal) {
      if (newVal) {
        this.$nextTick(() => {
          this.syncFilterStateToSkeleton();
        });
      }
    },
  },
  /**
   * Reset component state on destroy to prevent stale data
   */
  beforeDestroy() {
    // Component cleanup if needed
  },
};
</script>

<style lang="css" scoped>
/* Component-specific styles */
 .gotopage {
  color: #828285 !important;
}
</style>
